import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import './lvl2.css';
import { useSound } from '../hooks/useSound';
import { useProgress } from '../hooks/useProgress';
import { getLevelConfig } from '../utils/levelConfig';

const CHARACTERS = [
  { id: 'star',     label: 'Star',     emoji: '⭐', color: '#FFD930', glow: '#FFD93088' },
  { id: 'mushroom', label: 'Mushroom', emoji: '🍄', color: '#FF69B4', glow: '#FF69B488' },
  { id: 'alien',    label: 'Alien',    emoji: '👽', color: '#39FF14', glow: '#39FF1488' },
  { id: 'ghost',    label: 'Ghost',    emoji: '👻', color: '#C0C0C0', glow: '#C0C0C088' },
  { id: 'planet',   label: 'Planet',   emoji: '🪐', color: '#18B8E8', glow: '#18B8E888' },
];

const CORRECT_ORDER = ['star', 'ghost', 'planet', 'mushroom', 'alien'];

const RIDDLE_CLUES = [
  'Five beings — Star, Ghost, Planet, Mushroom, and Alien — are racing to finish the cosmic feast!',
  'Ghost does not finish first or last.',
  'Planet finishes after Ghost.',
  'Mushroom finishes before Alien.',
  'Star finishes before everyone else.',
  'Alien finishes last.',
  'Mushroom does not finish third.',
  'Arrange them in order, then enter the 5-digit code using each character\u2019s number.',
];

const MESSAGES = [
  'Drag a character to start!',
  'Nice thinking! 🧠',
  'Read the clues carefully!',
  "You're getting warmer! 🔥",
  'Almost cracked it!',
  'Great detective work!',
  'Think it through!',
  "You've got this!",
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function generateNumberMap() {
  const numbers = shuffle([1, 2, 3, 4, 5]);
  const map = {};
  CHARACTERS.forEach((c, i) => { map[c.id] = numbers[i]; });
  return map;
}

const Lvl2 = () => {
  const navigate = useNavigate();
  const { playHover, playClick, playDrop, playError, playVictory } = useSound();
  const { completeLevel } = useProgress();
  const config = getLevelConfig(2);

  const [closing,    setClosing]    = useState(false);
  const [showInfo,   setShowInfo]   = useState(true);
  const [gameStarted,setGameStarted]= useState(false);

  const [numberMap,     setNumberMap]     = useState(generateNumberMap);
  const [slots,         setSlots]         = useState([null, null, null, null, null]);
  const [bank,          setBank]          = useState(() => shuffle(CHARACTERS.map(c => c.id)));
  const [dragItem,      setDragItem]      = useState(null);
  const [checkedClues,  setCheckedClues]  = useState([]);
  const [codeInput,     setCodeInput]     = useState('');
  const [codeError,     setCodeError]     = useState(false);
  const [codeShake,     setCodeShake]     = useState(false);
  const [msgIndex,      setMsgIndex]      = useState(0);
  const [gameOver,      setGameOver]      = useState(false);
  const [flashWin,      setFlashWin]      = useState(false);
  const [wrongAttempts, setWrongAttempts] = useState(0);

  const contRef    = useRef(null);


  const levelNumRef   = useRef(null);
  const charRef       = useRef(null);
  const missionBoxRef = useRef(null);
  const btnRowRef     = useRef(null);
  const infoOverRef   = useRef(null);

  useEffect(() => {
    if (!showInfo || !infoOverRef.current) return;

    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    tl.fromTo(infoOverRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 0.4 }
    );
    tl.fromTo(levelNumRef.current,
      { opacity: 0, y: -30, scale: 0.85 },
      { opacity: 1, y: 0,   scale: 1, duration: 0.6, ease: 'back.out(1.4)' },
      '-=0.1'
    );
    tl.fromTo(charRef.current,
      { opacity: 0, x: -40, scale: 0.8 },
      { opacity: 1, x: 0,   scale: 1, duration: 0.65, ease: 'back.out(1.6)' },
      '-=0.3'
    );
    tl.fromTo(missionBoxRef.current,
      { opacity: 0, y: 28 },
      { opacity: 1, y: 0,   duration: 0.55 },
      '-=0.35'
    );
    tl.fromTo(btnRowRef.current,
      { opacity: 0, y: 18 },
      { opacity: 1, y: 0,   duration: 0.45 },
      '-=0.2'
    );
  }, [showInfo]);

  useEffect(() => {
    if (showInfo || !contRef.current) return;

    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });


    tl.fromTo(contRef.current,
      { opacity: 0, y: 20, scale: 0.97 },
      { opacity: 1, y: 0,  scale: 1, duration: 0.7 }
    );


    const topBar = contRef.current.querySelector('.lvl2-topbar');
    if (topBar) {
      tl.fromTo(topBar,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5 },
        '-=0.4'
      );
    }


    const charWrap = contRef.current.querySelector('.lvl2-char-wrap');
    if (charWrap) {
      tl.fromTo(charWrap,
        { opacity: 0, x: -40 },
        { opacity: 1, x: 0, duration: 0.55, ease: 'back.out(1.5)' },
        '-=0.3'
      );
    }

    // Clue box fades upward
    const clueBox = contRef.current.querySelector('.lvl2-clue-box');
    if (clueBox) {
      tl.fromTo(clueBox,
        { opacity: 0, y: 22 },
        { opacity: 1, y: 0, duration: 0.5 },
        '-=0.3'
      );
    }


    const bankChars = contRef.current.querySelectorAll('.lvl2-bank-char');
    if (bankChars.length) {
      tl.fromTo(bankChars,
        { opacity: 0, scale: 0.7, y: 14 },
        { opacity: 1, scale: 1,   y: 0, duration: 0.4, stagger: 0.07, ease: 'back.out(1.8)' },
        '-=0.2'
      );
    }


    const slots = contRef.current.querySelectorAll('.lvl2-slot');
    if (slots.length) {
      tl.fromTo(slots,
        { opacity: 0, y: 18 },
        { opacity: 1, y: 0, duration: 0.4, stagger: 0.06 },
        '-=0.25'
      );
    }


    const codeRow = contRef.current.querySelector('.lvl2-code-row');
    if (codeRow) {
      tl.fromTo(codeRow,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.35 },
        '-=0.1'
      );
    }

    return () => tl.kill();
  }, [showInfo]);

  const startPuzzle = () => {
    setClosing(true);
    // Animate intro out
    if (infoOverRef.current) {
      gsap.to(infoOverRef.current, { opacity: 0, y: -20, duration: 0.5, ease: 'power2.in' });
    }
    setTimeout(() => {
      setShowInfo(false);
      setGameStarted(true);
    }, 800);
  };

  const onDragStart = (e, id, from, idx) => {
    setDragItem({ id, from, idx });
    e.dataTransfer.effectAllowed = 'move';
    playClick();
  };

  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  const dropOnSlot = (e, slotIdx) => {
    e.preventDefault();
    if (!dragItem) return;

    const newSlots = [...slots];
    const newBank  = [...bank];
    const existingInSlot = newSlots[slotIdx];

    if (dragItem.from === 'bank') {
      if (existingInSlot) newBank.push(existingInSlot);
      newBank.splice(newBank.indexOf(dragItem.id), 1);
      newSlots[slotIdx] = dragItem.id;
    } else {
      const srcIdx = dragItem.idx;
      newSlots[srcIdx] = existingInSlot || null;
      newSlots[slotIdx] = dragItem.id;
    }

    setSlots(newSlots);
    setBank(newBank.filter(Boolean));
    setDragItem(null);
    playDrop();
    setMsgIndex(i => ((i + 1) % MESSAGES.length === 0 ? 1 : (i + 1) % MESSAGES.length));
  };

  const dropOnBank = (e) => {
    e.preventDefault();
    if (!dragItem || dragItem.from === 'bank') { setDragItem(null); return; }
    const newSlots = [...slots];
    const newBank  = [...bank];
    newSlots[dragItem.idx] = null;
    newBank.push(dragItem.id);
    setSlots(newSlots);
    setBank(newBank);
    setDragItem(null);
    playClick();
  };

  const toggleClue = (i) => {
    setCheckedClues(prev =>
      prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]
    );
  };

  const derivedCode  = slots.map(id => (id ? String(numberMap[id]) : '_')).join('');
  const correctCode  = CORRECT_ORDER.map(id => numberMap[id]).join('');

  const handleEnterCode = () => {
    if (gameOver) return;
    playClick();

    const isCorrect = codeInput.trim() === correctCode || derivedCode === correctCode;

    if (isCorrect) {
      const earnedStars = wrongAttempts === 0 ? 3 : wrongAttempts <= 2 ? 2 : 1;
      const earnedCoins = config.baseCoins + earnedStars * 5;
      completeLevel(2, earnedStars, earnedCoins);

      setGameOver(true);
      setFlashWin(true);
      playVictory();
      setTimeout(() => navigate('/victory', {
        state: { level: 2, stars: earnedStars, coins: earnedCoins, replayPath: '/level2' }
      }), 1500);
    } else {
      setWrongAttempts(a => a + 1);
      setCodeError(true);
      setCodeShake(true);
      playError();
      setTimeout(() => setCodeShake(false), 600);
      setTimeout(() => setCodeError(false), 2000);
    }
  };

  const handleExit  = () => { playClick(); navigate(-1); };

  const handleReset = () => {
    if (gameOver) return;
    playClick();
    setNumberMap(generateNumberMap());
    setBank(shuffle(CHARACTERS.map(c => c.id)));
    setSlots([null, null, null, null, null]);
    setCodeInput('');
    setCodeError(false);
    setCheckedClues([]);
    setMsgIndex(0);
    setWrongAttempts(0);
    if (contRef.current) {
      gsap.fromTo(contRef.current,
        { scale: 0.96, opacity: 0.5 },
        { scale: 1,    opacity: 1, duration: 0.55, ease: 'back.out(1.7)' }
      );
    }
  };

  const ordinalLabel = (i) => ['1ST','2ND','3RD','4TH','5TH'][i] || `${i+1}TH`;

  return (
    <main className="level2">

      {showInfo && (
        <div
          className={`levelinfo ${closing ? 'levelinfo-exit' : ''}`}
          ref={infoOverRef}
          style={{ opacity: 0 }} /* GSAP animates opacity in */
        >
          <div className="levelcont2" ref={levelNumRef}>
            <h1>LEVEL 2</h1>
            <h4 className="lvlinfosub">Riddle Pals</h4>
          </div>

          <section className="levelinstructions">
            <div className="missionchar222" ref={charRef}></div>
            <article className="missionblue" ref={missionBoxRef}>
              <h5 className="missiontitleblue">MISSION</h5>
              <h5 className="missionsubblue">Read the riddle and swap the characters.</h5>
              <h5 className="missioniblue">
                Each character is wearing a random number. Use the clues to figure out the right order,
                drag them into place, then enter the 5-digit code using each character's number.
              </h5>
            </article>
          </section>

          <article className="btncontainer" ref={btnRowRef}>
            <button className="btn1" onClick={startPuzzle}              onMouseEnter={playHover}>START PUZZLE</button>
            <button className="btn2" onClick={() => { playClick(); navigate(-1); }} onMouseEnter={playHover}>BACK</button>
          </article>
        </div>
      )}

      {flashWin && <div className="lvl2-win-flash" />}

      <section className="level2cont" ref={contRef}>

        <div className="lvl2-topbar">
          <button className="lvl2-exit-btn" onClick={handleExit} onMouseEnter={playHover}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
            EXIT
          </button>

          <div className="lvl2-title-block">
            <h1>LEVEL 2</h1>
          </div>

          <button className="lvl2-reset-btn" onClick={handleReset} onMouseEnter={playHover}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>
            </svg>
            RESET
          </button>
        </div>

        <div className="lvl2-body">

          <div className="lvl2-left">
            <div className="lvl2-char-wrap">
              <div className="lvl2-glowzy"></div>
              <div className="lvl2-char-bubble">
                <div className="lvl2-char-name">GLOWZY</div>
                <div className="lvl2-char-msg">{MESSAGES[msgIndex]}</div>
              </div>
            </div>
          </div>

          <div className="lvl2-center">

            <div className="lvl2-clue-box">
              <p className="lvl2-clue-intro">{RIDDLE_CLUES[0]}</p>
              <ul className="lvl2-clue-list">
                {RIDDLE_CLUES.slice(1).map((clue, i) => (
                  <li key={i} className="lvl2-clue-item">
                    <button
                      className={`lvl2-checkbox ${checkedClues.includes(i) ? 'checked' : ''}`}
                      onClick={() => toggleClue(i)}
                      onMouseEnter={playHover}
                    >
                      {checkedClues.includes(i) && <span className="lvl2-check">✓</span>}
                    </button>
                    <span className={checkedClues.includes(i) ? 'lvl2-clue-done' : ''}>{clue}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lvl2-bank" onDragOver={onDragOver} onDrop={dropOnBank}>
              {bank.map(id => {
                const c = CHARACTERS.find(x => x.id === id);
                return (
                  <div
                    key={id}
                    className="lvl2-bank-char"
                    draggable
                    onDragStart={e => onDragStart(e, id, 'bank', null)}
                    onMouseEnter={playHover}
                    style={{ borderColor: `${c.color}88`, boxShadow: `0 0 12px ${c.color}44` }}
                  >
                    <span className="lvl2-char-number-badge">{numberMap[id]}</span>
                    <span className="lvl2-bank-emoji">{c.emoji}</span>
                  </div>
                );
              })}
              {bank.length === 0 && (
                <p className="lvl2-bank-empty">All characters placed!</p>
              )}
            </div>

            <div className="lvl2-slots-row">
              {slots.map((id, i) => {
                const c = id ? CHARACTERS.find(x => x.id === id) : null;
                return (
                  <div key={i} className={`lvl2-slot ${id ? 'filled' : ''}`} onDragOver={onDragOver} onDrop={e => dropOnSlot(e, i)}>
                    {id ? (
                      <div
                        className="lvl2-slot-char"
                        draggable
                        onDragStart={e => onDragStart(e, id, 'slot', i)}
                        onMouseEnter={playHover}
                        style={{
                          background:  `radial-gradient(circle at 35% 35%, ${c.color}44, ${c.color}11)`,
                          boxShadow:   `0 0 20px ${c.color}66`,
                          borderColor: `${c.color}88`
                        }}
                      >
                        <span className="lvl2-char-number-badge">{numberMap[id]}</span>
                        <span className="lvl2-slot-emoji">{c.emoji}</span>
                      </div>
                    ) : (
                      <span className="lvl2-slot-placeholder">?</span>
                    )}
                    <span className="lvl2-slot-label">{ordinalLabel(i)}</span>
                  </div>
                );
              })}
            </div>

            <div className="lvl2-code-row">
              <input
                className={`lvl2-code-input ${codeShake ? 'shake' : ''} ${codeError ? 'error' : ''}`}
                type="text"
                maxLength={5}
                placeholder="ENTER CODE"
                value={codeInput}
                onChange={e => setCodeInput(e.target.value.replace(/[^0-9]/g, ''))}
                onKeyDown={e => e.key === 'Enter' && handleEnterCode()}
                spellCheck={false}
                inputMode="numeric"
              />
              <button className="lvl2-enter-btn" onClick={handleEnterCode} onMouseEnter={playHover}>
                ENTER CODE
              </button>
            </div>

            {codeError && (
              <p className="lvl2-error-msg">Wrong order! Try again. 💡</p>
            )}

          </div>
        </div>
      </section>
    </main>
  );
};

export default Lvl2;